import { useMemo } from "react";
import { PROCEDURE_CATEGORY_COLORS } from "@/components/treatment/procedureColors";

// =====================================================================
// TxPlanCategoryLegend — chip strip below the Tx Plan chart that names
// each procedure category present in the active plan, painted with
// that category's representative hue. Mirrors the multi-tooth legend
// already next to it; sits inside TreatmentTab so both the Patient
// profile Tx Plan tab AND the Doctor chairside Tx Plan mode show it
// (those views share the same component tree).
// =====================================================================

export default function TxPlanCategoryLegend({
  items = [],
  procedures = [],
  showProcedures = false,
  label = null,
  // When true, drop the standalone card chrome (rounded box, border,
  // background, top margin) and render just the labelled row so the
  // strip can be embedded inside a shared legend container — used by
  // the Doctor chairside footer, where it sits under the whole-mouth
  // status row inside one unified box.
  bare = false,
}) {
  const procById = useMemo(
    () => Object.fromEntries((procedures || []).map((p) => [p.id, p])),
    [procedures],
  );

  const entries = useMemo(() => {
    const seen = new Map();
    for (const it of items || []) {
      if (!it || it.status === "Cancelled" || it.status === "Declined") continue;
      if (showProcedures) {
        const proc = procById[it.procedure_id] || {};
        const name = it.procedure_name || proc.name || it.name;
        if (!name) continue;
        const cat = it.procedure_category || proc.category || "Other";
        if (!seen.has(name)) {
          seen.set(name, {
            label: name,
            color: it.procedure_color || proc.color_hex || PROCEDURE_CATEGORY_COLORS[cat] || "#94a3b8",
          });
        }
        continue;
      }
      // Prefer the live category off the procedure record; fall back
      // to whatever the item snapshot carries. Items without a
      // resolvable category are bucketed under "Other" so the chart
      // is never silently incomplete.
      const cat =
        it.procedure_category ||
        procById[it.procedure_id]?.category ||
        "Other";
      if (!seen.has(cat)) {
        seen.set(cat, { label: cat, color: PROCEDURE_CATEGORY_COLORS[cat] || "#94a3b8" });
      }
    }
    // Sort alphabetically so the legend reads consistently across
    // refreshes regardless of item insertion order.
    return Array.from(seen.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((entry) => ({ ...entry }));
  }, [items, procById, showProcedures]);

  if (entries.length === 0) return null;

  const title = label || (showProcedures ? "Procedures" : "Categories");
  const testId = showProcedures ? "tx-plan-procedure-legend" : "tx-plan-category-legend";

  return (
    <div
      className={
        bare
          ? "flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2"
          : "mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2 py-2 rounded-lg bg-slate-50 border border-slate-200"
      }
      data-testid={testId}
      aria-label={showProcedures ? "Procedures painted in this chart mode" : "Procedure categories on this plan"}
    >
      <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        {title}
      </span>
      {entries.map((e) => (
        <span
          key={e.label}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700"
          data-testid={`${testId}-${e.label}`}
        >
          <span
            aria-hidden
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: e.color }}
          />
          {e.label}
        </span>
      ))}
    </div>
  );
}
